/**
 * Route guard: the admin app is super_admin-gated.
 *
 *   - while the session bootstraps      → spinner
 *   - no identity (no session)          → redirect to /login (preserve intended path)
 *   - identity but NOT super_admin       → /access-denied
 *   - super_admin                        → render the protected subtree
 *
 * The gate decision uses `identity.isSuperAdmin`, which is computed from the
 * full `system_roles` list (never the lossy scalar).
 */
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

export interface RequireSuperAdminProps {
  children?: ReactNode;
}

export function RequireSuperAdmin({ children }: RequireSuperAdminProps): ReactNode {
  const { identity, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500" role="status">
        <span className="animate-pulse">Checking access…</span>
      </div>
    );
  }

  if (!identity) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!identity.isSuperAdmin) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
